import { useDispatch, useSelector } from 'react-redux';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import useCustomMetricPointContext from '../CustomWidget/useCustomWidgetPointContext';
import { selectTeamId } from 'state/actions';
import useCustomWidget from '../CustomWidget/useCustomWidget';
import { toggleModal } from 'state/callers';
import isEmpty from 'lodash/isEmpty';
import EmptyStateCustomPmWidget from '../CustomWidget/EmptyStates/EmptyStateCustomPmWidget';
import { LabelsSection } from '../../GraphBox/LabelsSection';
import { myAccountSelector } from 'state/selectors/account';
import { GRAPH_TYPES } from '../consts/graphTypes';
import usePMMetricsFilters from '../usePMMetricsFilters';
import { useContext } from 'react';
import PmDataContext from 'common/contexts/PmDataContext';

const usePMCustomWidget = ({ graph, filters, editConfig, widgetMetrics }) => {
  const { issueTypes } = useContext(PmDataContext);

  const widgetFilters = usePMMetricsFilters({ graph, issueTypes });
  const dispatch = useDispatch();

  const defaultCustomWidgetProps = useCustomWidget({
    graph,
    filters,
    widgetMetrics
  });

  const { headerProps, graphProps, EmptyState, loadingMetrics } =
    defaultCustomWidgetProps;

  const selectedTeam = useSelector(selectedTeamSelector);
  const account = useSelector(myAccountSelector);

  const contextFunctions = useCustomMetricPointContext({
    filters,
    selectedTeam,
    selectTeamId: id => dispatch(selectTeamId(id)),
    metrics: widgetMetrics.data,
    graph,
    metricName: widgetMetrics.name
  });

  const isEmptyFilters = Object.values(widgetFilters || {}).every(filter =>
    isEmpty(filter)
  );

  const isEmptyStateMetric = editConfig.isEditable && isEmptyFilters;
  let PmEmptyState = EmptyState;

  // TODO: check why sometimes we dont see the empty state
  if (!loadingMetrics && isEmptyStateMetric) {
    PmEmptyState = (
      <EmptyStateCustomPmWidget
        toggleModal={toggleModal}
        widget={graph}
        {...editConfig}
        issueTypes={issueTypes.data}
      />
    );
  }
  const noMatchResults =
    !loadingMetrics &&
    !graphProps[GRAPH_TYPES.BAR].yData?.filter(Boolean).length;

  if (noMatchResults) {
    PmEmptyState = null;
  }

  return {
    ...defaultCustomWidgetProps,
    headerProps: {
      ...headerProps,
      formattedAverage: PmEmptyState ? null : headerProps.formattedAverage,
      isPmCustomDashboard: true,
      showHelp: false
    },
    graphProps: {
      ...graphProps,
      [GRAPH_TYPES.BAR]: {
        ...graphProps[GRAPH_TYPES.BAR],
        onPointClick: contextFunctions.pm,
        isPmCustomDashboard: true
      }
    },
    EmptyState: PmEmptyState,
    isEmptyStateMetric,
    noMatchResults,
    issueTypes: issueTypes.data,
    widget: {
      ...graph,
      agg: graph.agg ?? widgetMetrics.agg,
      filters: widgetFilters
    },
    widgetExtraElements: [
      /* eslint-disable react/jsx-key */
      <LabelsSection
        filters={widgetFilters ?? []}
        widget={{
          ...graph,
          agg: graph.agg ?? widgetMetrics.agg,
          filters: widgetFilters
        }}
        issueTypes={issueTypes.data}
        loading={issueTypes.loading}
        account={account}
        {...editConfig}
      />
    ]
    /* eslint-enable react/jsx-key */
  };
};

export default usePMCustomWidget;
